import axios, { type AxiosInstance } from 'axios';

import { createAuthRefresh } from 'axios-auth-refresh';
import { storage, StorageKeys } from '@/services/storage';

import { GO_API_URL, GX_API_URL, GX_DATA_URL, GX_GO_TOKEN, GX_INTERNAL_URL } from '@/config/env';
import { appEvents } from '@/services/events';

export { AxiosError } from 'axios';

const MAX_REFRESH_ATTEMPTS = 3;

export function createRefreshTripToken(
  getGxToken: () => string | null
): () => Promise<string | null> {
  const refreshClient = axios.create({ baseURL: GX_INTERNAL_URL });
  return async () => {
    const gxToken = getGxToken();
    if (!gxToken) return null;
    for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
      try {
        const res = await refreshClient.get<{ token?: string }>('/token', {
          params: { token: gxToken },
          headers: { 'x-gx-go-token': GX_GO_TOKEN }
        });
        const token = res.data?.token;
        if (token) return token;
      } catch {
        // retry on next attempt
      }
    }
    return null;
  };
}

export interface HttpConfig {
  getTripToken?: () => string | null;
  setTripToken?: (token: string) => void;
  getGxToken?: () => string | null;
  refreshTripToken?: () => Promise<string | null>;
  onAuthExpired?: () => void;
}

export function createHttpClients(cfg: HttpConfig = {}) {
  const getTripToken = cfg.getTripToken ?? (() => null);
  const setTripToken = cfg.setTripToken ?? (() => undefined);
  const getGxToken = cfg.getGxToken ?? (() => null);
  const refreshTripToken = cfg.refreshTripToken ?? (() => Promise.resolve(null));
  const onAuthExpired =
    cfg.onAuthExpired ??
    (() => {
      appEvents.emit('user:logout', { reason: 'expired' });
    });

  let pending: Promise<string | null> | null = null;
  const refresh = () =>
    (pending ??= refreshTripToken()
      .then(token => {
        if (token) setTripToken(token);
        return token;
      })
      .finally(() => {
        pending = null;
      }));

  const attach = (instance: AxiosInstance, withStaticToken: boolean) => {
    instance.interceptors.request.use(req => {
      const token = getTripToken();
      if (token) req.headers.set('Authorization', `Bearer ${token}`);
      if (withStaticToken && GX_GO_TOKEN) req.headers.set('x-gx-go-token', GX_GO_TOKEN);
      return req;
    });
    createAuthRefresh(
      instance,
      async failed => {
        const newToken = await refresh();
        if (!newToken) {
          onAuthExpired();
          throw failed;
        }
      },
      { statusCodes: [401], deduplicateRefresh: true }
    );
    instance.interceptors.response.use(
      r => r,
      error => {
        const url: string = error.config?.url ?? '';
        const isAuthRoute =
          url.includes('/login') || url.includes('/onboarding') || url.includes('/token');
        const status: number | undefined = error.response?.status;
        // Only an unauthenticated session (no gx token) treats a 403/error as terminal.
        // For a logged-in user a 403 is a per-resource denial, not expiry (expiry is the
        // 401 auth-refresh above failing).
        if (!isAuthRoute && !getGxToken()) {
          if (status === 403) onAuthExpired();
          else if (status !== undefined && status !== 401 && status !== 404) {
            appEvents.emit('navigation:notfound', undefined);
          }
        }
        return Promise.reject(error);
      }
    );
  };

  const gx = axios.create({ baseURL: GX_API_URL });
  const go = axios.create({ baseURL: GO_API_URL });
  const data = axios.create({ baseURL: GX_DATA_URL });
  const internal = axios.create({ baseURL: GX_INTERNAL_URL });
  const gxUnauthenticated = axios.create({ baseURL: GX_API_URL });

  attach(gx, false);
  attach(go, true);
  attach(data, true);
  attach(internal, true);

  return { gx, go, data, internal, gxUnauthenticated };
}

const getStoredGxToken = () => storage.get(StorageKeys.GxToken);

export const http = createHttpClients({
  getTripToken: () => storage.get(StorageKeys.TripToken),
  setTripToken: token => {
    storage.set(StorageKeys.TripToken, token);
  },
  // Wired so the interceptor can tell an unauthenticated 403 from a per-resource one.
  getGxToken: getStoredGxToken,
  refreshTripToken: createRefreshTripToken(getStoredGxToken)
});
